import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBranding } from '../../contexts/BrandingContext';
import { useCRM } from '../../contexts/CRMContext';
import { clientPortalAPI, messagesAPI } from '../../utils/crmApi';
import {
    UserIcon,
    DocumentTextIcon,
    CalendarIcon,
    ChatBubbleLeftRightIcon,
    CreditCardIcon,
    MapPinIcon,
    ClockIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    BellIcon,
    Cog6ToothIcon,
    ArrowRightIcon,
    PlusIcon,
    EyeIcon,
    ArrowDownTrayIcon,
    ShareIcon,
    HeartIcon,
    StarIcon,
    ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useDialog, ConfirmationDialog } from '../../components/kibo-ui/dialog';
import {
    TableProvider,
    Table,
    TableHeader,
    TableSearch,
    TableHead,
    TableBody,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableFooter,
    StatusBadge,
    TableAvatar
} from '../../components/kibo-ui/table';
import TaskDetailView from './TaskDetailView';
import MessageList from './MessageList';
import CookieConsent from './CookieConsent';

const ClientPortal = () => {
    const { branding } = useBranding();
    const { currentAgency, user, switchAgency, loading: crmLoading } = useCRM();
    const { openDialog } = useDialog();
    const navigate = useNavigate();
    const [clientData, setClientData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [notifications, setNotifications] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [messages, setMessages] = useState([]);
    const [unreadMessageCount, setUnreadMessageCount] = useState(0);
    const [billing, setBilling] = useState(null);
    
    // Admin/agency user state
    const [isAdminView, setIsAdminView] = useState(false);
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [clientsList, setClientsList] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(false);
    
    // Task detail view state
    const [selectedTask, setSelectedTask] = useState(null);
    const [showTaskDetail, setShowTaskDetail] = useState(false);
    
    const tabsScrollRef = useRef(null);

    const isClientUser = () => {
        if (!user || !Array.isArray(user.agencyMemberships) || !currentAgency) return false;
        if (user.isSuperadmin) return false;
        const membership = user.agencyMemberships.find(m => String(m.agencyId) === String(currentAgency._id) || String(m.agencyId?._id) === String(currentAgency._id));
        return membership?.role === 'client';
    };

    const clientAgencies = (user?.agencyMemberships || []).filter(m => m.role === 'client').map(m => m.agencyId?._id || m.agencyId);

    // Get personalized colors based on agency branding
    const getPersonalizedColors = () => {
        const primaryColor = branding.primaryColor || '#3B82F6'; // Default blue
        const secondaryColor = branding.secondaryColor || '#10B981'; // Default green
        
        return {
            primary: primaryColor,
            secondary: secondaryColor,
            primaryLight: `${primaryColor}20`, // 20% opacity
            primaryDark: `${primaryColor}80`, // 80% opacity
            secondaryLight: `${secondaryColor}20`,
            secondaryDark: `${secondaryColor}80`
        };
    };

    const personalizedColors = getPersonalizedColors();

    // Status colors with personalized branding
    const getStatusColor = (status) => {
        const colors = {
            'completed': 'bg-green-100 dark:bg-green-950/20 text-green-700 dark:text-green-300',
            'in_progress': `bg-blue-100 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300`,
            'not_started': 'bg-muted text-foreground',
            'pending': 'bg-yellow-100 dark:bg-yellow-950/20 text-yellow-700 dark:text-yellow-300',
            'overdue': 'bg-red-100 dark:bg-red-950/20 text-red-700 dark:text-red-300',
            'cancelled': 'bg-muted text-foreground',
            'blocked': 'bg-red-100 dark:bg-red-950/20 text-red-700 dark:text-red-300',
            'draft': 'bg-muted text-foreground',
            'sent': `bg-blue-100 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300`,
            'paid': 'bg-green-100 dark:bg-green-950/20 text-green-700 dark:text-green-300',
            'unread': `bg-blue-100 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300`,
            'read': 'bg-muted text-foreground'
        };
        return colors[status] || 'bg-muted text-foreground';
    };

    // Priority colors
    const getPriorityColor = (priority) => {
        const colors = {
            'high': 'bg-red-100 dark:bg-red-950/20 text-red-700 dark:text-red-300',
            'medium': 'bg-yellow-100 dark:bg-yellow-950/20 text-yellow-700 dark:text-yellow-300',
            'low': 'bg-green-100 dark:bg-green-950/20 text-green-700 dark:text-green-300',
            'urgent': 'bg-purple-100 dark:bg-purple-950/20 text-purple-700 dark:text-purple-300'
        };
        return colors[priority] || 'bg-muted text-foreground';
    };

    // Determine if user is admin/agency user or client
    const checkUserRole = () => {
        if (!user || !user.agencyMemberships) return false;
        
        const currentMembership = user.agencyMemberships.find(
            m => m.agencyId.toString() === currentAgency?._id?.toString()
        );
        
        return currentMembership?.role === 'agency_admin' || 
               currentMembership?.role === 'agency_user' ||
               user.isSuperadmin;
    };

    // Check authentication and redirect if needed
    useEffect(() => {
        // Wait for CRM context to finish loading
        if (crmLoading) return;
        
        // If no user and no token, redirect to login
        const token = localStorage.getItem('crm_token');
        if (!user && !token) {
            console.warn('⚠️ No user and no token - redirecting to login');
            navigate('/login', { replace: true });
            return;
        }
        
        // If no user but token exists, user might have been cleared due to auth error
        // The crmApi interceptor should have handled redirect, but if not, redirect here
        if (!user && token) {
            console.warn('⚠️ User is null but token exists - authentication may have failed');
            // Don't redirect immediately - let crmApi interceptor handle it first
            // If we get here, it means the interceptor didn't redirect, so redirect now
            setTimeout(() => {
                if (!user) {
                    navigate('/login', { replace: true });
                }
            }, 100);
            return;
        }
        
        // Only proceed if user exists
        if (!user) return;
        
        const isAdmin = checkUserRole();
        setIsAdminView(isAdmin);
        
        if (isAdmin) {
            loadClientsList();
        } else {
            loadClientData();
        }
    }, [user, currentAgency, crmLoading, navigate]);

    // Periodic refresh for unread counts (simulating real-time notifications)
    useEffect(() => {
        // Don't set up polling if user is not authenticated
        if (!user || crmLoading) return;
        
        const interval = setInterval(() => {
            if (!isAdminView || selectedClientId) {
                loadUnreadMessageCount(isAdminView ? selectedClientId : null).then(setUnreadMessageCount);
            }
        }, 30000); // Check every 30 seconds

        return () => clearInterval(interval);
    }, [isAdminView, selectedClientId, user, crmLoading]);

    // Load data when client is selected (for admin view) or when client data changes
    useEffect(() => {
        // Don't load data if user is not authenticated
        if (!user || crmLoading) return;
        
        if (!isAdminView) {
            // Regular client viewing their own data - load once on mount
            loadClientData();
            loadNotifications();
            loadDocuments();
            loadTasks();
            loadAppointments();
            loadMessages();
            loadBilling();
            loadUnreadMessageCount().then(setUnreadMessageCount);
        }
        // Admin view data loading is handled by selectedClientId changes
    }, [isAdminView, user, crmLoading]);

    // Separate effect for admin client selection
    useEffect(() => {
        if (isAdminView && selectedClientId) {
            // Admin viewing specific client
            loadClientDataForClient(selectedClientId);
            loadNotificationsForClient(selectedClientId);
            loadDocumentsForClient(selectedClientId);
            loadTasksForClient(selectedClientId);
            loadAppointmentsForClient(selectedClientId);
            loadMessagesForClient(selectedClientId);
            loadBillingForClient(selectedClientId);
            loadUnreadMessageCount(selectedClientId).then(setUnreadMessageCount);
        } else if (isAdminView && !selectedClientId) {
            // Clear data when no client selected
            setClientData(null);
            setNotifications([]);
            setDocuments([]);
            setTasks([]);
            setAppointments([]);
            setMessages([]);
            setBilling(null);
        }
    }, [selectedClientId, isAdminView]);

    // Load clients list for admin users
    const loadClientsList = async () => {
        // Don't make API call if user is not authenticated
        if (!user) {
            console.warn('⚠️ Cannot load clients list - user is not authenticated');
            return;
        }
        
        try {
            setClientsLoading(true);
            const response = await clientPortalAPI.getClientsList();
            if (response.data?.success) {
                setClientsList(response.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load clients list:', error);
            
            // Handle authentication errors
            if (error.response?.status === 401) {
                const token = localStorage.getItem('crm_token');
                if (!token) {
                    navigate('/login', { replace: true });
                }
                return; // Don't show error toast for auth errors
            }
            
            toast.error('Failed to load clients list');
        } finally {
            setClientsLoading(false);
        }
    };

    // Load client data for selected client (admin view) or current user (client view)
    const loadClientDataForClient = async (clientId = null) => {
        // Don't make API call if user is not authenticated
        if (!user) {
            console.warn('⚠️ Cannot load client data - user is not authenticated');
            return;
        }
        
        try {
            setLoading(true);
            const response = await clientPortalAPI.getProfile(clientId);
            console.log('Client portal profile response:', response);
            if (response.data?.success) {
                console.log('Client data received:', response.data.data);
                // If data is null (admin without client selection), clear client data
                if (response.data.data === null) {
                    setClientData(null);
                    return; // Don't show error, just clear data
                }
                setClientData(response.data.data);
                try {
                    if (response.data.data?.name) {
                        localStorage.setItem('client_display_name', response.data.data.name);
                    }
                } catch (_) {}
            } else {
                console.error('Failed to load client data - no success flag');
                // Only show error if not an admin without client selection
                if (!isAdminView || clientId) {
                    toast.error('Failed to load client data');
                }
            }
        } catch (error) {
            console.error('Failed to load client data:', error);
            console.error('Error response:', error.response?.data);
            
            // Handle authentication errors
            if (error.response?.status === 401) {
                // Authentication error - crmApi interceptor should handle redirect
                // But if we get here, user might have been cleared
                const token = localStorage.getItem('crm_token');
                if (!token) {
                    navigate('/login', { replace: true });
                }
                return; // Don't show error toast for auth errors
            }
            
            // Only show error toast if it's not a 404 for admin without selection
            if (!(error.response?.status === 404 && isAdminView && !clientId)) {
                toast.error(error.response?.data?.message || 'Failed to load client data');
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle client selection change (admin view)
    const handleClientSelection = (clientId) => {
        setSelectedClientId(clientId);
        // Data will be loaded by useEffect when selectedClientId changes
    };

    const loadClientData = async () => {
        return loadClientDataForClient();
    };

    // Client-specific loading functions for admin view
    const loadNotificationsForClient = async (clientId) => {
        return loadNotifications(clientId);
    };

    const loadDocumentsForClient = async (clientId) => {
        return loadDocuments(clientId);
    };

    const loadTasksForClient = async (clientId) => {
        return loadTasks(clientId);
    };

    const loadAppointmentsForClient = async (clientId) => {
        return loadAppointments(clientId);
    };

    const loadMessagesForClient = async (clientId) => {
        return loadMessages(clientId);
    };

    const loadBillingForClient = async (clientId) => {
        return loadBilling(clientId);
    };

    // Regular loading functions (with optional clientId parameter)
    const loadNotifications = async (clientId = null) => {
        try {
            const response = await clientPortalAPI.getNotifications(clientId);
            if (response.data.success) {
                setNotifications(response.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load notifications:', error);
            setNotifications([]);
        }
    };

    const loadDocuments = async (clientId = null) => {
        try {
            const response = await clientPortalAPI.getDocuments(clientId);
            if (response.data.success) {
                setDocuments(response.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load documents:', error);
            setDocuments([]);
        }
    };

    const loadTasks = async (clientId = null) => {
        try {
            const response = await clientPortalAPI.getTasks(clientId);
            if (response.data.success) {
                setTasks(response.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load tasks:', error);
            setTasks([]);
        }
    };

    const loadAppointments = async (clientId = null) => {
        try {
            const response = await clientPortalAPI.getAppointments(clientId);
            if (response.data.success) {
                setAppointments(response.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load appointments:', error);
            setAppointments([]);
        }
    };

    const loadMessages = async (clientId = null) => {
        try {
            const response = await clientPortalAPI.getMessages(clientId);
            if (response.data.success) {
                setMessages(response.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load messages:', error);
            setMessages([]);
        }
    };

    const loadUnreadMessageCount = async (clientId = null) => {
        try {
            const response = await messagesAPI.getUnreadCount(clientId);
            if (response.data.success) {
                return response.data.count || 0;
            }
        } catch (error) {
            console.error('Failed to load unread message count:', error);
        }
        return 0;
    };

    const loadBilling = async (clientId = null) => {
        try {
            const response = await clientPortalAPI.getBilling(clientId);
            if (response.data.success) {
                setBilling(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load billing:', error);
            setBilling(null);
        }
    };

    const handleDownloadDocument = async (documentId) => {
        try {
            const response = await clientPortalAPI.downloadDocument(documentId);
            // Create blob and download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `document-${documentId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading document:', error);
            toast.error('Failed to download document');
        }
    };

    const handleSendMessage = async (message) => {
        try {
            await clientPortalAPI.sendMessage(message);
            toast.success('Message sent successfully!');
            loadMessages();
            loadUnreadMessageCount().then(setUnreadMessageCount);
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message');
        }
    };

    const handleMarkNotificationRead = async (notificationId) => {
        try {
            await clientPortalAPI.markNotificationRead(notificationId);
            loadNotifications();
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const handleUpdateProfile = async (profileData) => {
        try {
            await clientPortalAPI.updateProfile(profileData);
            toast.success('Profile updated successfully!');
            loadClientData();
        } catch (error) {
            console.error('Error updating profile:', error);
            toast.error('Failed to update profile');
        }
    };

    // Task detail view handlers
    const handleViewTask = (task) => {
        setSelectedTask(task);
        setShowTaskDetail(true);
    };

    const handleCloseTaskDetail = () => {
        setSelectedTask(null);
        setShowTaskDetail(false);
    };

    // Note: Edit and delete functionality removed for client view
    // Clients can only view task details, not modify them

    const formatCurrency = (amount, currency = 'EUR') => {
        return new Intl.NumberFormat('en-EU', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const formatDate = (date) => {
        if (!date) return 'No due date';
        try {
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) return 'Invalid date';
            return dateObj.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return 'Invalid date';
        }
    };

    const formatDateTime = (date) => {
        if (!date) return 'No date';
        try {
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) return 'Invalid date';
            return dateObj.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return 'Invalid date';
        }
    };

    // Format status for display
    const formatStatus = (status) => {
        if (!status) return 'Unknown';
        // Handle snake_case and convert to Title Case
        return status
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    // Format priority for display
    const formatPriority = (priority) => {
        if (!priority) return 'Normal';
        return priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
    };

    // Combine all activities into a unified timeline
    const recentActivities = useMemo(() => {
        const activities = [];
        
        // Add tasks
        tasks.forEach(task => {
            activities.push({
                id: `task-${task._id}`,
                type: 'task',
                icon: ClipboardDocumentListIcon,
                title: task.title || 'Task',
                description: task.status ? `Status: ${formatStatus(task.status)}` : '',
                timestamp: new Date(task.createdAt || task.updatedAt || Date.now()),
                data: task,
                color: task.status === 'completed' ? 'text-green-600' : 
                       task.status === 'in_progress' ? 'text-blue-600' : 
                       'text-gray-600'
            });
        });
        
        // Add documents
        documents.forEach(doc => {
            activities.push({
                id: `doc-${doc._id}`,
                type: 'document',
                icon: DocumentTextIcon,
                title: doc.name || doc.fileName || 'Document',
                description: doc.type ? `Type: ${doc.type}` : 'Document shared',
                timestamp: new Date(doc.createdAt || doc.uploadedAt || Date.now()),
                data: doc,
                color: 'text-blue-600'
            });
        });
        
        // Add messages (recent messages)
        messages.slice(0, 5).forEach(msg => {
            activities.push({
                id: `msg-${msg._id}`,
                type: 'message',
                icon: ChatBubbleLeftRightIcon,
                title: 'New Message',
                description: msg.content ? (msg.content.length > 50 ? msg.content.substring(0, 50) + '...' : msg.content) : 'Message received',
                timestamp: new Date(msg.createdAt || msg.sentAt || Date.now()),
                data: msg,
                color: 'text-green-600'
            });
        });
        
        // Add appointments
        appointments.forEach(apt => {
            activities.push({
                id: `apt-${apt._id}`,
                type: 'appointment',
                icon: CalendarIcon,
                title: apt.subject || apt.title || 'Appointment',
                description: apt.startAt ? `Scheduled: ${formatDateTime(apt.startAt)}` : 'Appointment scheduled',
                timestamp: new Date(apt.startAt || apt.createdAt || Date.now()),
                data: apt,
                color: 'text-purple-600'
            });
        });
        
        // Add notifications
        notifications.forEach(notif => {
            activities.push({
                id: `notif-${notif._id}`,
                type: 'notification',
                icon: BellIcon,
                title: notif.title || 'Notification',
                description: notif.message || '',
                timestamp: new Date(notif.createdAt || Date.now()),
                data: notif,
                color: notif.status === 'unread' ? 'text-blue-600' : 'text-gray-600',
                isUnread: notif.status === 'unread'
            });
        });
        
        // Sort by timestamp (most recent first) and return top 10
        return activities
            .sort((a, b) => b.timestamp - a.timestamp)
            .slice(0, 10);
    }, [tasks, documents, messages, appointments, notifications]);

    // Calculate counts for badges
    const tabCounts = {
        tasks: tasks.length,
        documents: documents.length,
        appointments: appointments.length,
        messages: unreadMessageCount, // Unread messages
        notifications: notifications.filter(n => n.status === 'unread').length, // Unread notifications
    };

    const tabs = [
        { id: 'overview', name: 'Overview', icon: UserIcon },
        { id: 'documents', name: 'Documents', icon: DocumentTextIcon, count: tabCounts.documents },
        { id: 'tasks', name: 'Tasks', icon: CheckCircleIcon, count: tabCounts.tasks },
        { id: 'appointments', name: 'Appointments', icon: CalendarIcon, count: tabCounts.appointments },
        { id: 'messages', name: 'Messages', icon: ChatBubbleLeftRightIcon, count: tabCounts.messages },
        { id: 'billing', name: 'Billing', icon: CreditCardIcon },
        { id: 'notifications', name: 'Notifications', icon: BellIcon, count: tabCounts.notifications },
        { id: 'settings', name: 'Settings', icon: Cog6ToothIcon }
    ];

    // Show loading spinner while CRM context is loading or while loading client data
    if (crmLoading || loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }
    
    // If no user after loading completes, don't render (redirect will happen in useEffect)
    if (!user) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Redirecting to login...</p>
                </div>
            </div>
        );
    }

    // Show message if admin view but no client selected
    if (isAdminView && !selectedClientId) {
        return (
            <div className="space-y-6">
                {/* Agency Cover Photo Header */}
                <div className="relative h-48 rounded-lg overflow-hidden shadow-lg">
                    {branding.agencyCoverImage ? (
                        <img
                            src={branding.agencyCoverImage}
                            alt={`${branding.agencyName || currentAgency?.name} Cover`}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div 
                            className="w-full h-full flex items-center justify-center"
                            style={{ 
                                background: `linear-gradient(135deg, ${personalizedColors.primary} 0%, ${personalizedColors.secondary} 100%)`
                            }}
                        >
                            <div className="text-center text-white">
                                <div className="text-6xl font-bold opacity-20 mb-2">
                                    {(branding.agencyName || currentAgency?.name || 'A').charAt(0).toUpperCase()}
                                </div>
                                <p className="text-sm opacity-80">Welcome to your portal</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Client Selection */}
                <div className="p-6 bg-blue-50 dark:bg-blue-950/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-400">Client Portal Preview</h3>
                            <p className="text-sm text-blue-700 dark:text-blue-300">Select a client to preview their portal experience</p>
                        </div>
                        <div className="w-64">
                            <select
                                value={selectedClientId || ''}
                                onChange={(e) => handleClientSelection(e.target.value || null)}
                                className="w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                disabled={clientsLoading}
                            >
                                <option value="">Select a client...</option>
                                {clientsList.map((client) => (
                                    <option key={client._id} value={client._id}>
                                        {client.name} ({client.email})
                                    </option>
                                ))}
                            </select>
                            {clientsLoading && (
                                <p className="text-xs text-muted-foreground mt-1">Loading clients...</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Empty state */}
                <div className="text-center py-12">
                    <UserIcon className="mx-auto h-12 w-12 text-muted-foreground" />
                    <h3 className="mt-2 text-sm font-medium text-foreground">No client selected</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Choose a client from the dropdown above to preview their portal.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Agency Cover Photo Header */}
            <div className="relative h-48 rounded-lg overflow-hidden shadow-lg">
                {branding.agencyCoverImage ? (
                    <img
                        src={branding.agencyCoverImage}
                        alt={`${branding.agencyName || currentAgency?.name} Cover`}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ 
                            background: `linear-gradient(135deg, ${personalizedColors.primary} 0%, ${personalizedColors.secondary} 100%)`
                        }}
                    >
                        <div className="text-center text-white">
                            <div className="text-6xl font-bold opacity-20 mb-2">
                                {(branding.agencyName || currentAgency?.name || 'A').charAt(0).toUpperCase()}
                            </div>
                            <p className="text-sm opacity-80">Welcome to your portal</p>
                        </div>
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute inset-0 opacity-10" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }} />
                <div className="absolute bottom-4 left-4 text-white">
                    <h1 className="text-2xl font-bold">
                        {branding.agencyName || currentAgency?.name || 'Your Agency'}
                    </h1>
                    <p className="text-sm opacity-90">
                        {branding.agencyMotto || 'Your Relocation Partner'}
                    </p>
                </div>
                <div className="absolute top-4 right-4">
                    <div className="bg-card/20 backdrop-blur-sm rounded-lg px-3 py-1">
                        <p className="text-white text-xs font-medium">Client Portal</p>
                    </div>
                </div>
            </div>

            {/* Header with Agency Branding */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-6 -mt-8 relative z-10">
                {/* Agency Branding Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
                    <div className="flex items-center space-x-4">
                        {branding.agencyLogo ? (
                            <img
                                src={branding.agencyLogo}
                                alt={`${branding.agencyName || currentAgency?.name} Logo`}
                                className="h-12 w-12 object-contain rounded-lg"
                            />
                        ) : (
                            <div className="h-12 w-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-lg">
                                    {(branding.agencyName || currentAgency?.name || 'A').charAt(0).toUpperCase()}
                                </span>
                            </div>
                        )}
                        <div>
                            <h2 className="text-xl font-bold text-foreground">
                                {branding.agencyName || currentAgency?.name || 'Your Agency'}
                            </h2>
                            <p className="text-sm text-muted-foreground">
                                {branding.agencyMotto || 'Your Relocation Partner'}
                            </p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-sm text-muted-foreground">Client Portal</p>
                        <p className="text-xs text-muted-foreground">
                            Powered by LiveLoveSpain CRM
                        </p>
                    </div>
                </div>

                {/* Multi-agency selector for client users (no global dropdown) */}
                {isClientUser() && clientAgencies.length > 1 && (
                    <div className="mb-4 p-2 bg-muted border rounded-lg">
                        <div className="text-xs text-muted-foreground mb-2">Your agencies</div>
                        <div className="flex flex-wrap gap-2">
                            {user.agencyMemberships.filter(m => m.role === 'client').map((m) => {
                                const agencyId = m.agencyId?._id || m.agencyId;
                                const isActive = String(agencyId) === String(currentAgency?._id);
                                return (
                                    <button
                                        key={String(agencyId)}
                                        onClick={async () => {
                                            try { await switchAgency(agencyId); } catch (e) { /* noop */ }
                                        }}
                                        className={`px-3 py-1.5 text-xs rounded-full border ${isActive ? 'bg-blue-50 dark:bg-blue-950/10 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300' : 'bg-card hover:bg-muted'}`}
                                    >
                                        {m.agencyName || currentAgency?.name || 'Agency'}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Admin Client Selection (if admin view) */}
                {isAdminView && (
                    <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-400">Client Portal Preview</h3>
                                <p className="text-sm text-blue-700 dark:text-blue-300">Select a client to preview their portal experience</p>
                            </div>
                            <div className="w-64">
                                <select
                                    value={selectedClientId || ''}
                                    onChange={(e) => handleClientSelection(e.target.value || null)}
                                    className="w-full px-3 py-2 border border-input rounded-md shadow-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    disabled={clientsLoading}
                                >
                                    <option value="">Select a client...</option>
                                    {clientsList.map((client) => (
                                        <option key={client._id} value={client._id}>
                                            {client.name} ({client.email})
                                        </option>
                                    ))}
                                </select>
                                {clientsLoading && (
                                    <p className="text-xs text-muted-foreground mt-1">Loading clients...</p>
                                )}
                            </div>
                        </div>
                        {selectedClientId && clientData && (
                            <div className="mt-3 p-3 bg-card rounded border">
                                <p className="text-sm text-muted-foreground">
                                    <strong>Previewing:</strong> {clientData.name} ({clientData.email})
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Client Welcome Section */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center flex-1 min-w-0">
                        <div className="flex-shrink-0">
                            {clientData?.profileImage ? (
                                <img
                                    src={clientData.profileImage}
                                    alt={`${clientData?.name || 'Client'} profile`}
                                    className="h-12 w-12 sm:h-16 sm:w-16 rounded-full object-cover border-2 border-border"
                                />
                            ) : (
                                <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                                    <UserIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
                                </div>
                            )}
                        </div>
                        <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                            <h1 className="text-lg sm:text-2xl font-bold text-foreground truncate">
                                Welcome back, {clientData?.name || 'Client'}!
                            </h1>
                            <p className="text-xs sm:text-sm text-muted-foreground truncate">
                                {clientData?.email || 'client@example.com'}
                            </p>
                            <div className="flex items-center mt-2 flex-wrap gap-2">
                                <StatusBadge 
                                    status={clientData?.status || 'active'}
                                    className={getStatusColor(clientData?.status || 'active')}
                                >
                                    {clientData?.status || 'Active'}
                                </StatusBadge>
                                <span className="text-xs sm:text-sm text-muted-foreground">
                                    Client since {formatDate(clientData?.createdAt || new Date())}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 mt-4 sm:mt-0 flex-wrap sm:flex-nowrap">
                        <button className="inline-flex items-center px-3 sm:px-4 py-2 border border-border rounded-lg shadow-sm text-xs sm:text-sm font-medium text-foreground bg-background hover:bg-muted w-full sm:w-auto justify-center">
                            <ShareIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                            Share Portal
                        </button>
                        <Link
                            to="/client-portal/settings"
                            className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent rounded-lg shadow-sm text-xs sm:text-sm font-medium text-white hover:opacity-90 transition-opacity w-full sm:w-auto justify-center"
                            style={{ 
                                backgroundColor: personalizedColors.primary,
                                '--hover-color': personalizedColors.primaryDark
                            }}
                        >
                            <Cog6ToothIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                            Settings
                        </Link>
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-border bg-background sticky top-0 z-10 relative">
                <div 
                    ref={tabsScrollRef}
                    className="overflow-x-auto scrollbar-thin"
                >
                    <nav className="flex space-x-0.5 min-w-max px-2 sm:px-4 md:px-6">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`
                                    group relative flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 text-sm font-medium transition-all duration-200
                                    whitespace-nowrap hover:bg-muted rounded-t-lg
                                    ${
                                        activeTab === tab.id
                                            ? 'text-foreground bg-muted'
                                            : 'text-muted-foreground hover:text-foreground'
                                    }
                                `}
                            >
                                {/* Active indicator */}
                                {activeTab === tab.id && (
                                    <span
                                        className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t"
                                        style={{ backgroundColor: personalizedColors.primary }}
                                    />
                                )}
                                
                                <tab.icon 
                                    className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 transition-colors ${
                                        activeTab === tab.id 
                                            ? 'text-primary' 
                                            : 'text-muted-foreground group-hover:text-foreground'
                                    }`} 
                                />
                                <span className="hidden sm:inline">{tab.name}</span>
                                
                                {/* Badge */}
                                {tab.count !== undefined && tab.count > 0 && (
                                    <span
                                        className={`
                                            inline-flex items-center justify-center min-w-[18px] h-5 px-1.5 rounded-full text-xs font-semibold
                                            transition-colors
                                            ${activeTab === tab.id
                                                ? 'bg-blue-100 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300'
                                                : 'bg-muted text-muted-foreground group-hover:bg-muted/80'
                                            }
                                        `}
                                    >
                                        {tab.count > 99 ? '99+' : tab.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
                <div className="space-y-6">
                    {/* Personalized Welcome Message */}
                    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <div 
                                    className="h-12 w-12 rounded-full flex items-center justify-center"
                                    style={{ backgroundColor: personalizedColors.primaryLight }}
                                >
                                    <HeartIcon className="h-6 w-6" style={{ color: personalizedColors.primary }} />
                                </div>
                            </div>
                            <div className="ml-4 flex-1">
                                <h3 className="text-lg font-semibold text-foreground mb-2">
                                    Welcome to your personalized portal!
                                </h3>
                                <p className="text-muted-foreground mb-4">
                                    {branding.agencyMotto || 
                                     `We're here to make your relocation to Spain as smooth as possible. ` +
                                     `Track your progress, access important documents, and stay connected with our team.`}
                                </p>
                                <div className="flex items-center text-sm text-muted-foreground">
                                    <span className="font-medium">Your dedicated consultant:</span>
                                    <span className="ml-2">{user?.name || 'Our Team'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <DocumentTextIcon className="h-8 w-8" style={{ color: personalizedColors.primary }} />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-muted-foreground">Documents</p>
                                    <p className="text-2xl font-semibold text-foreground">{documents.length}</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <CheckCircleIcon className="h-8 w-8" style={{ color: personalizedColors.secondary }} />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-muted-foreground">Completed Tasks</p>
                                    <p className="text-2xl font-semibold text-foreground">
                                        {tasks.filter(t => t.status === 'completed').length}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <CalendarIcon className="h-8 w-8" style={{ color: '#8B5CF6' }} />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-muted-foreground">Upcoming Appointments</p>
                                    <p className="text-2xl font-semibold text-foreground">
                                        {appointments.filter(a => new Date(a.date) > new Date()).length}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <BellIcon className="h-8 w-8" style={{ color: '#F59E0B' }} />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-muted-foreground">Unread Notifications</p>
                                    <p className="text-2xl font-semibold text-foreground">
                                        {notifications.filter(n => n.status === 'unread').length}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recent Activity */}
                    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Recent Activity</h3>
                        <div className="space-y-4">
                            {recentActivities.length > 0 ? (
                                recentActivities.map((activity) => {
                                    const Icon = activity.icon;
                                    return (
                                        <div 
                                            key={activity.id} 
                                            className={`flex items-start p-3 rounded-lg transition-colors ${
                                                activity.isUnread ? 'bg-blue-50/50 dark:bg-blue-950/10 border border-blue-200 dark:border-blue-800' : 'hover:bg-muted/50'
                                            }`}
                                        >
                                            <div className="flex-shrink-0">
                                                <div className={`p-2 rounded-lg ${
                                                    activity.type === 'task' ? 'bg-blue-100 dark:bg-blue-900/30' :
                                                    activity.type === 'document' ? 'bg-purple-100 dark:bg-purple-900/30' :
                                                    activity.type === 'message' ? 'bg-green-100 dark:bg-green-900/30' :
                                                    activity.type === 'appointment' ? 'bg-orange-100 dark:bg-orange-900/30' :
                                                    'bg-gray-100 dark:bg-gray-800'
                                                }`}>
                                                    <Icon className={`h-4 w-4 ${activity.color}`} />
                                                </div>
                                            </div>
                                            <div className="ml-3 flex-1 min-w-0">
                                                <p className="text-sm font-medium text-foreground">{activity.title}</p>
                                                {activity.description && (
                                                    <p className="text-xs text-muted-foreground mt-1">{activity.description}</p>
                                                )}
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    {formatDateTime(activity.timestamp)}
                                                </p>
                                            </div>
                                            {activity.isUnread && (
                                                <div className="flex-shrink-0 ml-2">
                                                    <div className="h-2 w-2 rounded-full bg-blue-500" />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-center py-8">
                                    <ClockIcon className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                                    <p className="text-sm text-muted-foreground">No recent activity</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <button
                                onClick={() => setActiveTab('documents')}
                                className="flex items-center p-4 border border-border rounded-lg hover:bg-muted"
                            >
                                <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-3" />
                                <div className="text-left">
                                    <p className="text-sm font-medium text-foreground">View Documents</p>
                                    <p className="text-xs text-muted-foreground">Access your files</p>
                                </div>
                                <ArrowRightIcon className="h-4 w-4 text-muted-foreground ml-auto" />
                            </button>
                            <button
                                onClick={() => setActiveTab('messages')}
                                className="flex items-center p-4 border border-border rounded-lg hover:bg-muted"
                            >
                                <ChatBubbleLeftRightIcon className="h-6 w-6 text-green-600 mr-3" />
                                <div className="text-left">
                                    <p className="text-sm font-medium text-foreground">Send Message</p>
                                    <p className="text-xs text-muted-foreground">Contact your agent</p>
                                </div>
                                <ArrowRightIcon className="h-4 w-4 text-muted-foreground ml-auto" />
                            </button>
                            <button
                                onClick={() => setActiveTab('billing')}
                                className="flex items-center p-4 border border-border rounded-lg hover:bg-muted"
                            >
                                <CreditCardIcon className="h-6 w-6 text-purple-600 mr-3" />
                                <div className="text-left">
                                    <p className="text-sm font-medium text-foreground">View Billing</p>
                                    <p className="text-xs text-muted-foreground">Check invoices</p>
                                </div>
                                <ArrowRightIcon className="h-4 w-4 text-muted-foreground ml-auto" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-foreground">Your Documents</h3>
                        <button 
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: personalizedColors.primary }}
                        >
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Upload Document
                        </button>
                    </div>
                    
                    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-muted">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Document</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-card divide-y divide-gray-200">
                                {documents.map((document) => (
                                    <tr key={document._id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <DocumentTextIcon className="h-8 w-8 text-blue-600 mr-3" />
                                                <div>
                                                    <div className="text-sm font-medium text-foreground">{document.name}</div>
                                                    <div className="text-sm text-muted-foreground">{document.description}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {document.type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <StatusBadge 
                                                status={document.status}
                                                className={getStatusColor(document.status)}
                                            >
                                                {document.status}
                                            </StatusBadge>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                            {formatDate(document.createdAt)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleDownloadDocument(document._id)}
                                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                                >
                                                    <ArrowDownTrayIcon className="h-4 w-4" />
                                                </button>
                                                <button className="text-muted-foreground hover:text-foreground">
                                                    <EyeIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Tasks Tab */}
            {activeTab === 'tasks' && (
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-foreground">Your Tasks</h3>
                    </div>
                    
                    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-muted">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Task</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Priority</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Due Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-card divide-y divide-gray-200">
                                {tasks.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-12 text-center text-muted-foreground">
                                            No tasks found
                                        </td>
                                    </tr>
                                ) : (
                                    tasks.map((task) => (
                                        <tr 
                                            key={task._id} 
                                            className="hover:bg-muted cursor-pointer transition-colors"
                                            onClick={() => handleViewTask(task)}
                                        >
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0">
                                                        <CheckCircleIcon 
                                                            className={`h-5 w-5 ${
                                                                task.status === 'completed' 
                                                                    ? 'text-green-600 dark:text-green-400' 
                                                                    : 'text-muted-foreground/30'
                                                            }`} 
                                                        />
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-foreground">
                                                            {task.templateId?.title || task.title || 'Untitled Task'}
                                                        </div>
                                                        {task.templateId?.description || task.description ? (
                                                            <div className="text-sm text-muted-foreground mt-1">
                                                                {task.templateId?.description || task.description}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <StatusBadge 
                                                    status={task.priority}
                                                    className={getPriorityColor(task.priority)}
                                                >
                                                    {formatPriority(task.priority || 'medium')}
                                                </StatusBadge>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <StatusBadge 
                                                    status={task.status}
                                                    className={getStatusColor(task.status)}
                                                >
                                                    {formatStatus(task.status || 'not_started')}
                                                </StatusBadge>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                                {formatDate(task.dueDate || task.due_date)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <button 
                                                    className="text-blue-600 hover:text-blue-800 transition-colors"
                                                    title="View task details"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleViewTask(task);
                                                    }}
                                                >
                                                    <EyeIcon className="h-5 w-5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Messages Tab */}
            {activeTab === 'messages' && (
                <div className="space-y-6">
                    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
                        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-foreground">Messages</h3>
                            <p className="text-sm text-muted-foreground">Communicate with your agent and team</p>
                        </div>
                        <div className="h-[600px]">
                            <MessageList 
                                clientId={isAdminView ? selectedClientId : null}
                                isAdminView={isAdminView}
                                showHeader={false}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Other tabs content would go here */}
            {activeTab !== 'overview' && activeTab !== 'documents' && activeTab !== 'tasks' && activeTab !== 'messages' && (
                <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                    <div className="text-center">
                        <div className="mx-auto h-12 w-12 text-muted-foreground">
                            {(() => {
                                const activeTabData = tabs.find(tab => tab.id === activeTab);
                                const IconComponent = activeTabData?.icon;
                                return IconComponent ? <IconComponent className="h-12 w-12" /> : null;
                            })()}
                        </div>
                        <h3 className="mt-2 text-sm font-medium text-foreground">
                            {tabs.find(tab => tab.id === activeTab)?.name}
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                            This section will contain {activeTab} functionality.
                        </p>
                    </div>
                </div>
            )}

            {/* Task Detail View Modal */}
            {showTaskDetail && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[120]">
                    <div className="w-full max-w-6xl max-h-[95vh] overflow-y-auto scrollbar-modal">
                        <TaskDetailView
                            task={selectedTask}
                            onClose={handleCloseTaskDetail}
                        />
                    </div>
                </div>
            )}

            {/* Personalized Footer */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-6 mt-8">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        {branding.agencyLogo ? (
                            <img
                                src={branding.agencyLogo}
                                alt={`${branding.agencyName || currentAgency?.name} Logo`}
                                className="h-8 w-8 object-contain rounded"
                            />
                        ) : (
                            <div 
                                className="h-8 w-8 rounded flex items-center justify-center"
                                style={{ backgroundColor: personalizedColors.primary }}
                            >
                                <span className="text-white font-bold text-sm">
                                    {(branding.agencyName || currentAgency?.name || 'A').charAt(0).toUpperCase()}
                                </span>
                            </div>
                        )}
                        <div>
                            <p className="text-sm font-medium text-foreground">
                                {branding.agencyName || currentAgency?.name || 'Your Agency'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {branding.agencyMotto || 'Your Relocation Partner'}
                            </p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-xs text-muted-foreground">
                            Powered by LiveLoveSpain CRM
                        </p>
                        <p className="text-xs text-muted-foreground">
                            Need help? Contact us anytime
                        </p>
                    </div>
                </div>
            </div>

            {/* Cookie Consent Banner */}
            <CookieConsent />
        </div>
    );
};

export default ClientPortal;